/**
 * BFS over the one-letter-swap word graph + helpers that drive
 * per-keystroke validation in the play view.
 *
 * `validAtSlot[k]` is the set of words that lie on SOME shortest path
 * from start to target at slot k. The player's typed word at slot k is
 * accepted iff it's in validAtSlot[k] AND differs from the previous
 * word by exactly one letter.
 */
import type { AdjacencyList } from './types';

/** Returns a Map<word, distance> reachable from `start` via single-letter
 *  swaps, up to (and including) `maxDepth`. */
export function bfsDistances(
  start: string,
  graph: AdjacencyList,
  maxDepth = Infinity
): Map<string, number> {
  const dist = new Map<string, number>([[start, 0]]);
  const queue: string[] = [start];
  let head = 0;
  while (head < queue.length) {
    const current = queue[head++]!;
    const d = dist.get(current)!;
    if (d >= maxDepth) continue;
    for (const next of graph.get(current) ?? []) {
      if (dist.has(next)) continue;
      dist.set(next, d + 1);
      queue.push(next);
    }
  }
  return dist;
}

/** Compute the per-slot valid-word sets. Index 0 is the start slot
 *  (singleton {start}); index N is the target slot ({target}); 1..N-1 are
 *  the player-fillable intermediates. */
export function computeValidAtSlot(
  start: string,
  target: string,
  N: number,
  graph: AdjacencyList
): Set<string>[] {
  const fromStart = bfsDistances(start, graph, N);
  const fromTarget = bfsDistances(target, graph, N);
  const out: Set<string>[] = [];
  for (let k = 0; k <= N; k++) {
    const set = new Set<string>();
    for (const [word, d] of fromStart) {
      if (d !== k) continue;
      const dt = fromTarget.get(word);
      if (dt === undefined) continue;
      if (d + dt === N) set.add(word);
    }
    out.push(set);
  }
  return out;
}

export function letterDiff(a: string, b: string): number {
  if (a.length !== b.length) return -1;
  let d = 0;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) d++;
  return d;
}
